import { useContext, useEffect, useState, useCallback } from 'react';
import { Context } from 'components/Store';
import catchCancel from 'helpers/catchCancel';
import { parseServerError } from 'helpers/errorHelpers';
import { sortBy } from 'lodash';
import useInventoryApi from 'hooks/useInventoryApi';
import useInventoryV2Api from 'hooks/useInventoryV2Api';
import PAGINATION_SIZE from 'helpers/globalConstants';
import { productsMasterData as productsMasterDataAPI } from 'utilities/api';
import useInventoryProductsV2Flag from 'hooks/useInventoryProductsV2Flag';
import combineDuplicatedProducts from './helperFunction';

const useInventoryData = (runSideEffect = true, forceV1 = false) => {
  const [, dispatch] = useContext(Context);
  const useV2Products = useInventoryProductsV2Flag();
  const inventoryV1API = useInventoryApi('inventory/products');
  const inventoryV2API = useInventoryV2Api('inventory/products');
  const inventoryAPI =
    useV2Products && !forceV1 ? inventoryV2API : inventoryV1API;
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalInventoryProducts, setTotalInventoryProducts] = useState(0);

  const getInventoryMasterDetails = async filteredData => {
    const selectedOrgId = localStorage.getItem('selectedOrganizationId');
    const { promise } = await productsMasterDataAPI.fetch(selectedOrgId);
    return promise
      .then(data => {
        const combinedProducts = combineDuplicatedProducts(
          filteredData,
          data.data
        );
        setProducts(sortBy(combinedProducts, 'productName'));
        setTotalInventoryProducts(combinedProducts.length);
        setError(null);
      })
      .catch(catchCancel)
      .catch(err => {
        setError(err);
        parseServerError(dispatch)(err);
      })
      .finally(() => setLoading(false));
  };

  const getInventoryDetails = useCallback(
    (pageNo = 0, size = PAGINATION_SIZE) => {
      setLoading(true);
      const { promise } = inventoryAPI.fetch(undefined, {
        pageNo,
        size
      });
      promise
        .then(async ({ data }) => {
          await getInventoryMasterDetails(data.results);
          setError(null);
        })
        .catch(catchCancel)
        .catch(err => {
          setError(err);
          parseServerError(dispatch)(err);
        })
        .finally(() => setLoading(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inventoryAPI]
  );

  useEffect(() => {
    if (!runSideEffect) return;
    getInventoryDetails();
  }, [getInventoryDetails, runSideEffect]);

  return {
    products,
    setProducts,
    setLoading,
    loading,
    error,
    totalInventoryProducts,
    setTotalInventoryProducts,
    getInventoryDetails
  };
};

export default useInventoryData;
